import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { appPackageVersion } from '@/shared/store/package-meta'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'
import { isElectron, tauriMode } from '@/shared/utils/import-meta'

export const useAppVersionStore = defineStore('app-version', () => {
  const version = ref('')
  const actualVersion = ref('')

  const setVersion = (value: string) => {
    version.value = value
  }

  const NO_CACHE_HEADERS = {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
  }

  const appendNoCacheTimestamp = (url: string): string => {
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}_=${Date.now()}`
  }

  const fetchActualVersion = async (): Promise<string> => {
    try {
      const fetch = useFetchApi()
      // @TODO надо будет удалить после реализации обновления tauri
      if (tauriMode()) {
        const { check } = await import('@tauri-apps/plugin-updater')
        const update = await check()
        if (update?.available) {
          actualVersion.value = update?.version || ''

          return update?.version ?? ''
        } else {
          // В generic URL бэкенда может ещё не быть — грузим с текущего origin (localhost-плагин).
          const versionUrl = typeof location !== 'undefined'
            ? new URL('/version.json', location.origin).href
            : '/version.json'
          const res = await window.fetch(appendNoCacheTimestamp(versionUrl), {
            cache: 'no-store',
            headers: NO_CACHE_HEADERS,
          })
          if (!res.ok) {
            throw new Error(`version.json ${res.status}`)
          }
          const data = await res.json() as { version: string }
          actualVersion.value = data.version ?? ''
          return data.version ?? ''
        }
      } else if (isElectron()) {
        const url = appendNoCacheTimestamp(getAppURL({ pathName: '/version.json', isLocal: true }))
        const { data } = await fetch.get<{ version: string }>(url, {
          headers: NO_CACHE_HEADERS,
        })
        actualVersion.value = data.version
        return data.version
      } else {
        const url = appendNoCacheTimestamp('./version.json')
        const { data } = await fetch.get<{ version: string }>(url, {
          headers: NO_CACHE_HEADERS,
        })
        actualVersion.value = data.version
        return data.version
      }
    } catch {
      // URL сервера ещё нет (generic) или сеть — не ломаем приложение, показываем текущую версию.
      const fallback = version.value || appPackageVersion
      actualVersion.value = fallback
      return fallback
    }
  }

  const updateTauriApp = async () => {
    if (tauriMode()) {
      const { check } = await import('@tauri-apps/plugin-updater')
      const update = await check()
      if (update === null) {
        const { t } = useLocalization()
        throw new Error(t('common.actualVersionError'))
      } else if (update?.available) {
        await update.downloadAndInstall()
        const { relaunch } = await import('@tauri-apps/plugin-process')
        await relaunch()
      }
    }
  }

  return {
    actualVersion,
    version,
    setVersion,
    fetchActualVersion,
    updateTauriApp,
  }
})
